import { useState } from 'react';

import { ErroreApi } from '../api/client.ts';
import { autenticazione } from '../api/scadenziario.ts';
import { Marchio } from '../componenti/Marchio.tsx';
import { useAutenticazione } from '../auth/contesto.tsx';

/**
 * Cambio della password provvisoria, obbligatorio al primo accesso.
 *
 * Non è una schermata che si può saltare, e non è l'interfaccia a deciderlo:
 * finché l'obbligo è pendente il backend rifiuta ogni altra chiamata con
 * `CAMBIO_PASSWORD_RICHIESTO`. Qui si mostra la spiegazione invece di lasciare
 * che l'utente sbatta contro una serie di errori che non capisce.
 *
 * Al termine si torna al login: cambiare password chiude tutte le sessioni,
 * compresa quella in corso. È voluto — se si cambia perché si teme che
 * qualcuno l'abbia scoperta, lasciarne una viva vanifica l'operazione — ma va
 * detto prima, altrimenti sembra che qualcosa sia andato storto.
 */
export function CambioPassword() {
  const { utente, esci } = useAutenticazione();

  const [attuale, setAttuale] = useState('');
  const [nuova, setNuova] = useState('');
  const [conferma, setConferma] = useState('');
  const [errore, setErrore] = useState<string | null>(null);
  const [inCorso, setInCorso] = useState(false);
  const [fatto, setFatto] = useState(false);

  async function invia() {
    if (nuova !== conferma) {
      setErrore('Le due password non coincidono.');
      return;
    }

    setErrore(null);
    setInCorso(true);

    try {
      await autenticazione.cambiaPassword(attuale, nuova);
      setFatto(true);
    } catch (e) {
      setErrore(e instanceof ErroreApi ? e.message : 'Errore imprevisto');
      setInCorso(false);
    }
  }

  if (fatto) {
    return (
      <div className="accesso">
        <div className="accesso-riquadro">
          <div className="riga-marchio">
            <Marchio />
            <span className="nome-marchio">FMSchedule</span>
          </div>
          <h1 className="accesso-titolo">Password cambiata</h1>
          <p className="accesso-sottotitolo">
            Da adesso è l'unica valida, e la conosci solo tu. Rientra con quella nuova.
          </p>
          <button
            type="button"
            className="bottone bottone-principale"
            onClick={() => void esci()}
          >
            Vai all'accesso
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="accesso">
      <div className="accesso-riquadro">
        <div className="riga-marchio">
          <Marchio />
          <span className="nome-marchio">FMSchedule</span>
        </div>

        <h1 className="accesso-titolo">Scegli la tua password</h1>
        <p className="accesso-sottotitolo">
          Ciao {utente?.nome ?? ''}, quella che stai usando è provvisoria: te l'ha assegnata
          un amministratore, che quindi la conosce. Prima di entrare devi sceglierne una tua.
        </p>

        {errore !== null && <div className="avviso-errore" role="alert">{errore}</div>}

        <form onSubmit={(e) => { e.preventDefault(); void invia(); }}>
          <div className="campo">
            <label htmlFor="cp-attuale">Password provvisoria</label>
            <input
              id="cp-attuale"
              type="password"
              value={attuale}
              onChange={(e) => setAttuale(e.target.value)}
              autoComplete="current-password"
              required
              autoFocus
            />
          </div>

          <div className="campo">
            <label htmlFor="cp-nuova">Nuova password</label>
            <input
              id="cp-nuova"
              type="password"
              value={nuova}
              onChange={(e) => setNuova(e.target.value)}
              autoComplete="new-password"
              minLength={12}
              required
            />
            <p className="aiuto">
              Almeno 12 caratteri. Nessun obbligo di maiuscole o simboli: una frase lunga
              che ricordi è più sicura di «Password1!» e non finisce su un post-it.
            </p>
          </div>

          <div className="campo">
            <label htmlFor="cp-conferma">Ripeti la nuova password</label>
            <input
              id="cp-conferma"
              type="password"
              value={conferma}
              onChange={(e) => setConferma(e.target.value)}
              autoComplete="new-password"
              required
            />
          </div>

          <button type="submit" className="bottone bottone-principale" disabled={inCorso}>
            {inCorso ? 'Salvataggio…' : 'Salva e continua'}
          </button>

          <p className="aiuto">
            Dopo il cambio dovrai rientrare: cambiare password chiude tutte le sessioni
            aperte, anche questa.
          </p>
        </form>
      </div>
    </div>
  );
}
